// `/ae` — an AE's own book: every account they own, open opportunities and booked meetings first
// (GRO-360). The AE's landing page and their My Accounts tab (`lib/viewTabs.ts`).
//
// The board's table frame, fed by `/api/ae/book`, which the server has already cut down to the
// signed-in person's Salesforce-owned accounts. Nothing here filters for security: the page draws
// what it was sent. What is the AE's own: the four sections (`ae/aeSections.ts`), the columns
// (`ae/aeColumns.tsx`) and the summary counts (`ae/aeFacts.ts`). The account panel is the shared
// one; its Company info tab is the AE's own (`ae/AeCompanyInfo.tsx`), picked by the viewer's role
// in `lib/roleViews.ts`, as is the rail without Outreach.
//
// Read-only on purpose. An AE holds no push, enrich, request, assign or return grant, so the view
// carries no control for any of them: no Pin and Hide menu, and no action in the panel.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AccessDeniedError, fetchAeBook } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { aeColumns } from '../ae/aeColumns';
import { aeCounts } from '../ae/aeFacts';
import {
  AE_GROUP_LABELS, GROUP_CONVERSATION, GROUP_MEETING, GROUP_OPPORTUNITY, GROUP_REST, splitAeAccounts,
} from '../ae/aeSections';
import { BoardV2 } from '../book/BoardV2';
import { HighPriorityChip } from '../book/AccountFilterBar';
import { withPriorityColumn } from '../book/PriorityFlagButton';
import { highPriorityCount, keepHighPriority } from '../book/priorityFlag';
import { usePriorityToggle } from '../book/usePriorityToggle';
import { attachRanking, resolveWorkReason } from '../book/derived';
import { attachScores } from '../book/scores';
import { useScores } from '../book/useScores';
import type { RankedAccount } from '../book/derived';
import { WORKLIST_ORDER, cycleSort, sortRows } from '../book/sort';
import { formatCount } from '../lib/format';
import { Sidebar } from '../sidebar/Sidebar';
import type { BookAccount } from '../api/types';

// The sections are fixed and read-only: no board preference exists on this view.
function withSection(rows: RankedAccount[], group: number): RankedAccount[] {
  return rows.map(row => ({ ...row, board_pref: null, group }));
}

export function AeView() {
  const { getToken, email, signOut } = useAuthContext();
  const [searchParams] = useSearchParams();
  const accountParam = searchParams.get('account');

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(WORKLIST_ORDER);
  const [openAccountId, setOpenAccountId] = useState<string | null>(accountParam);
  // The High priority chip, and the flag's toggle. An AE sees the flag; admin and leadership
  // viewing as an AE may change it (the server decides).
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);
  const priority = usePriorityToggle(getToken);

  const bookQuery = useQuery({
    queryKey: ['ae-book'],
    queryFn: () => fetchAeBook(getToken),
  });
  const book = bookQuery.data;
  const viewer = book?.viewer;
  // Fit and Engagement after Amount, behind the board's own visibility rule (`book/useScores.ts`).
  const scores = useScores(getToken, viewer);
  const columns = useMemo(() => withPriorityColumn(aeColumns(scores.shown)), [scores.shown]);

  // Search, then the four sections, then the ranking join the board's cells read. The sections keep
  // their own order until a column is clicked; the group still sorts above any column sort.
  const rows = useMemo(() => {
    if (!book) return [] as RankedAccount[];
    const query = search.trim().toLowerCase();
    const searched: BookAccount[] = query
      ? book.accounts.filter(row => row.account.toLowerCase().includes(query))
      : book.accounts;
    const matching = keepHighPriority(searched, highPriorityOnly);
    const sections = splitAeAccounts(matching, book.today);
    const ranked = (accounts: BookAccount[]) =>
      attachRanking(attachScores(accounts, scores.payload), null, book.generated_at, book.today);
    return [
      ...withSection(ranked(sections.opportunities), GROUP_OPPORTUNITY),
      ...withSection(ranked(sections.meetings), GROUP_MEETING),
      ...withSection(ranked(sections.conversations), GROUP_CONVERSATION),
      ...withSection(ranked(sections.rest), GROUP_REST),
    ];
  }, [book, search, highPriorityOnly, scores.payload]);
  const sortedRows = useMemo(() => sortRows(rows, sort, columns), [rows, sort, columns]);

  const cellContext = useMemo(() => (book ? {
    today: new Date(book.today + 'T00:00:00Z'),
    staleDays: book.stale_days,
    fitCeiling: book.fit_score_ceiling,
    priority: { viewer: book.viewer, pendingId: priority.pendingId, onToggle: priority.toggle },
  } : null), [book, priority.pendingId, priority.toggle]);

  if (bookQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={bookQuery.error.message} email={email} onSignOut={signOut} />;
  }

  const openAccount = openAccountId
    ? sortedRows.find(row => row.id === openAccountId) ?? null
    : null;

  const body = () => {
    // Held for the scores too, so the first table drawn is already in its final order (GRO-491).
    if (bookQuery.isPending || scores.waiting) return <div className="loading">Loading your accounts…</div>;
    if (bookQuery.error) {
      return (
        <div className="err">
          <b>Could not load your accounts.</b>
          <br />
          {(bookQuery.error as Error).message}
        </div>
      );
    }
    if (!book || !cellContext) return null;

    // An admin or leader with no view-as is shown only accounts they own themselves, which is
    // usually none; say how to see an AE's page.
    if (viewer?.role !== 'ae') {
      return (
        <p className="actionnote" role="status">
          This page shows the accounts the signed-in person owns in Salesforce, which for you is
          none. To see an AE&rsquo;s page, choose them under &ldquo;Viewing as&rdquo; above.
        </p>
      );
    }

    const asOfDate = (book.freshness?.synced_at ?? '').slice(0, 10);
    // The AE's own counts, each labelled with what it counts (`aeCounts`), replace the board's
    // signal / talking / untouched line, so the board's stats go in as zeros and are not drawn.
    const counts = aeCounts(rows, book.today);
    const stats = { withSignal: 0, inCommunication: 0, untouched: 0, withMeeting: 0, openOpps: 0 };
    const totalCount = book.accounts.length;
    const countText = `${formatCount(sortedRows.length)} shown${
      sortedRows.length !== totalCount ? ' of ' + formatCount(totalCount) : ''}`;

    const controls = (
      <div className="controls scope densecontrolset">
        <input
          type="search"
          placeholder="Search account…"
          aria-label="Search account"
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <HighPriorityChip state={{
          pressed: highPriorityOnly,
          count: highPriorityCount(book.accounts),
          onToggle: () => setHighPriorityOnly(current => !current),
        }} />
      </div>
    );

    const notes = (
      <>
        {totalCount === 0 ? (
          <p className="actionnote" role="status">
            No accounts on the dashboard&rsquo;s book list you as the Salesforce owner. The book holds
            accounts that also have a strategist owner, so an account you own with none is not here.
          </p>
        ) : null}
        {priority.note ? (
          <p className="actionnote bad" role="status">
            {priority.note}{' '}
            <button type="button" className="striplink" onClick={priority.dismissNote}>Dismiss</button>
          </p>
        ) : null}
      </>
    );

    return (
      <BoardV2
        columns={columns}
        rows={sortedRows}
        context={cellContext}
        sort={sort}
        onSort={key => setSort(current => cycleSort(current, key, WORKLIST_ORDER))}
        openAccountId={openAccountId}
        onToggleAccount={accountId =>
          setOpenAccountId(current => (current === accountId ? null : accountId))}
        onSelectAccount={accountId => setOpenAccountId(accountId)}
        canEditPrefs={false}
        pendingPrefId={null}
        onSetPref={() => {}}
        brief={null}
        showBrief={false}
        stats={stats}
        counts={counts}
        tableClassName="aetable"
        countText={`Accounts you own · ${countText}`}
        unreadCount={0}
        asOf={asOfDate}
        briefPanel={null}
        notificationStrip={null}
        controls={controls}
        notes={notes}
        groupLabels={AE_GROUP_LABELS}
        showRowMenu={false}
      />
    );
  };

  return (
    <>
      <PageChrome freshness={book?.freshness ?? null} build={book?.build} viewer={viewer}>
        {body()}
      </PageChrome>

      <Sidebar
        account={openAccount}
        workReason={openAccount && book ? resolveWorkReason(openAccount, null, book.generated_at) : null}
        book={book ?? null}
        onClose={() => setOpenAccountId(null)}
        onContactEnriched={() => {}}
        page="ae"
      />
    </>
  );
}
